import { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const Wrapper = styled.div`
  z-index: 99;
  position: absolute;
  top: ${({ $posY }) => $posY};
  left: ${({ $posX }) => $posX};
`;

const Draggable = ({ posX, posY, children }) => {
  const wrapperRef = useRef();
  const active = useRef();
  const childRef = useRef();

  const setTranslate = useCallback((xPos, yPos) => {
    wrapperRef.current.style.transform = `translate3d(${xPos}px, ${yPos}px, 0px)`;
  }, []);

  const handleDrag = useCallback((e) => {
    if (!active.current) {
      return;
    }

    wrapperRef.current.currentX = e.clientX - wrapperRef.current.initialX;
    wrapperRef.current.currentY = e.clientY - wrapperRef.current.initialY;

    wrapperRef.current.xOffset = wrapperRef.current.currentX;
    wrapperRef.current.yOffset = wrapperRef.current.currentY;

    setTranslate(wrapperRef.current.currentX, wrapperRef.current.currentY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removePointerEventInIframe = useCallback(() => {
    if (active.current) {
      childRef.current.style.pointerEvents = 'none';
    }
  }, []);

  const handleDragStart = useCallback((e) => {
    document.addEventListener('mousemove', handleDrag);
    childRef.current.addEventListener('mousemove', removePointerEventInIframe);

    active.current = true;

    if (!wrapperRef.current.xOffset) {
      wrapperRef.current.xOffset = 0;
    }

    if (!wrapperRef.current.yOffset) {
      wrapperRef.current.yOffset = 0;
    }

    wrapperRef.current.initialX = e.clientX - wrapperRef.current.xOffset;
    wrapperRef.current.initialY = e.clientY - wrapperRef.current.yOffset;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = useCallback(() => {
    active.current = false;
    childRef.current.style.pointerEvents = 'auto';
    document.removeEventListener('mousemove', handleDrag);
    childRef.current.removeEventListener(
      'mousemove',
      removePointerEventInIframe
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper ref={wrapperRef} $posX={posX} $posY={posY}>
      {children({
        onMouseDown: handleDragStart,
        onMouseUp: handleDragEnd,
        childRef
      })}
    </Wrapper>
  );
};

export default Draggable;

Draggable.propTypes = {
  posX: PropTypes.string,
  posY: PropTypes.string,
  children: PropTypes.func.isRequired
};

Draggable.defaultProps = {
  posX: '0px',
  posY: '0px'
};
